import { useRealtimeMessages } from "@traas/lib/src/realtime/RealtimeConnectionContext";
import { FC } from "react";
import { useApi } from "../api/APIContext";
import { TaskAttachment } from "../../../lib/src/api/dto/TaskAttachment";
import { TaskSupplier } from "../../../lib/src/api/dto/TaskSupplier";
import { useRefresh } from "@traas/lib/src/realtime/RefreshController";

export const RealtimeMessageHandler: FC = () => {
  const {
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTasksUpdated,
    onTeamsUpdated,
    onSuppliersUpdated,
    onHolidaysUpdated,
    onEmployeesUpdated,
    onProjectsUpdated,
    onColorsUpdated,
    onNotesUpdated,
    onDailyRemarksUpdated,
    onQuantityTypesUpdated,
    onOrdersUpdated,
    onWorkordersUpdated,
    onWorkordersJobRan,
    onWorkordersJobWarning,
    onHoursUpdated,
    onEmergencyDetailsWarning,
    onEmergencyDetailsUpdated,
    onVisitCreated,
    onVisitUpdated,
    onVisitDeleted,
    onRelationsDeleted,
  } = useApi();

  const refresh = useRefresh();

  useRealtimeMessages((json) => {
    switch (json.type) {
      case "task_created":
        onTaskCreated({
          id: String(json.data.id),
          projectId: String(json.data.project_id),
          description: json.data.description,
          color: json.data.color,
          teamId: null,
          startAt: null,
          durationMinutes: null,
          suppliersStatus: "todo",
          suppliers: [],
          relatedSuppliers:
            json.data.relatedSuppliers.map((taskSupplier: any) => {
              return new TaskSupplier(
                taskSupplier["id"],
                taskSupplier["task_id"],
                taskSupplier["order_id"],
                taskSupplier["project_id"],
                taskSupplier["supplier_id"],
                taskSupplier["relation_id"],
                taskSupplier["description"],
                taskSupplier["status"],
                taskSupplier["is_complete"],
              );
            }) ?? [],
          taskAttachments: [],
          taskTemplateId: json.data.task_template_id,
          quantityType: json.data.quantity_type,
          quantityAmount: json.data.quantity_amount,
          quantityThickness: json.data.quantity_thickness,
          thicknessUnit: json.data.thickness_unit,
          status: json.data.status,
          isInvoiced: json.data.is_invoiced,
          isVoided: json.data.is_voided,
          isLocked: json.data.is_locked,
          isSent: json.data.sent,
          shouldGenerateWorkorder: json.data.should_generate_workorder,
          workorderRemark: json.data.workorder_remark ?? null,
          isWorkorderRemarkImportant: json.data.is_workorder_remark_important ?? false,
          projectRemark: json.data.project_remark ?? null,
          isProjectRemarkImportant: json.data.is_project_remark_important ?? false,
          todos: json.data.todos ?? {},
          workorderStatus: "unknown",
          workorderFeedback: null,
        });
        break;
      case "tasks_created":
        json.data.forEach((jsonItem: any) => {
          onTaskCreated({
            id: String(jsonItem.id),
            projectId: String(jsonItem.project_id),
            description: jsonItem.description,
            color: jsonItem.color,
            teamId: null,
            startAt: null,
            durationMinutes: null,
            suppliersStatus: "todo",
            suppliers: [],
            relatedSuppliers:
              jsonItem.related_suppliers.map((taskSupplier: any) => {
                return new TaskSupplier(
                  taskSupplier["id"],
                  taskSupplier["task_id"],
                  taskSupplier["order_id"],
                  taskSupplier["project_id"],
                  taskSupplier["supplier_id"],
                  taskSupplier["relation_id"],
                  taskSupplier["description"],
                  taskSupplier["status"],
                  taskSupplier["is_complete"],
                );
              }) ?? [],
            taskAttachments: [],
            taskTemplateId: json.data.task_template_id,
            quantityType: jsonItem.quantity_type,
            quantityAmount: jsonItem.quantity_amount,
            quantityThickness: json.data.quantity_thickness,
            thicknessUnit: json.data.thickness_unit,
            status: json.data.status,
            isInvoiced: jsonItem.is_invoiced,
            isVoided: jsonItem.is_voided,
            isLocked: json.data.is_locked,
            isSent: json.data.sent,
            shouldGenerateWorkorder: json.data.should_generate_workorder,
            workorderRemark: json.data.workorder_remark ?? null,
            isWorkorderRemarkImportant: json.data.is_workorder_remark_important ?? false,
            projectRemark: json.data.project_remark ?? null,
            isProjectRemarkImportant: json.data.is_project_remark_important ?? false,
            todos: json.data.todos ?? {},
            workorderStatus: "unknown",
            workorderFeedback: null,
          });
        });
        break;
      case "task_updated":
        onTaskUpdated({
          id: String(json.data.id),
          projectId: String(json.data.project_id),
          description: json.data.description,
          color: json.data.color,
          teamId: json.data.team_uuid ? String(json.data.team_uuid) : null,
          startAt: json.data.start_at ? new Date(json.data.start_at) : null,
          durationMinutes: json.data.duration ? Number(json.data.duration) : null,
          suppliersStatus: json.data.suppliers_status ?? "todo",
          suppliers:
            json.data.suppliers.map((taskSupplier: any) => {
              return new TaskSupplier(
                taskSupplier["id"],
                taskSupplier["task_id"],
                taskSupplier["order_id"],
                taskSupplier["project_id"],
                taskSupplier["supplier_id"],
                taskSupplier["relation_id"],
                taskSupplier["description"],
                taskSupplier["status"],
                taskSupplier["is_complete"],
              );
            }) ?? [],
          relatedSuppliers:
            json.data.related_suppliers.map((taskSupplier: any) => {
              return new TaskSupplier(
                taskSupplier["id"],
                taskSupplier["task_id"],
                taskSupplier["order_id"],
                taskSupplier["project_id"],
                taskSupplier["supplier_id"],
                taskSupplier["relation_id"],
                taskSupplier["description"],
                taskSupplier["status"],
                taskSupplier["is_complete"],
              );
            }) ?? [],
          taskAttachments:
            json.data.task_attachments.map((attachment: any) => {
              return new TaskAttachment(
                attachment["id"],
                attachment["planning_item_task_id"],
                attachment["name"],
                attachment["path"],
                attachment["url"],
                attachment["extension_type"],
              );
            }) ?? [],
          taskTemplateId: json.data.task_template_id,
          quantityType: json.data.quantity_type,
          quantityAmount: json.data.quantity_amount,
          quantityThickness: json.data.quantity_thickness,
          thicknessUnit: json.data.thickness_unit,
          status: json.data.status,
          isInvoiced: json.data.is_invoiced,
          isVoided: json.data.is_voided,
          isLocked: json.data.is_locked,
          isSent: json.data.sent,
          shouldGenerateWorkorder: json.data.should_generate_workorder,
          workorderRemark: json.data.workorder_remark ?? null,
          isWorkorderRemarkImportant: json.data.is_workorder_remark_important ?? false,
          todos: json.data.todos ?? {},
          workorderStatus: "unknown",
          workorderFeedback: null,
          projectRemark: json.data.project_remark ?? null,
          isProjectRemarkImportant: json.data.is_project_remark_important ?? false,
        });
        break;
      case "task_deleted":
        onTaskDeleted(String(json.data.id));
        break;
      case "teams_updated":
        onTeamsUpdated();
        break;
      case "tasks_updated":
        onTasksUpdated();
        break;
      case "suppliers_updated":
        onSuppliersUpdated();
        break;
      case "projects_updated":
        onProjectsUpdated();
        break;
      case "colors_updated":
        onColorsUpdated();
        break;
      case "quantity_types_updated":
        onQuantityTypesUpdated();
        break;
      case "notes_updated":
        onNotesUpdated();
        break;
      case "orders_updated":
        onOrdersUpdated();
        break;
      case "daily_remarks_updated":
        onDailyRemarksUpdated();
        break;
      case "workorders_updated":
        onWorkordersUpdated();
        break;
      case "hours_updated":
        onHoursUpdated();
        break;
      case "employees_updated":
        // Reload employees index in APIContext.
        onEmployeesUpdated();
        // Reload any detail call that may reload on this key (this should not cause double reloads).
        refresh("employee");
        break;
      case "holidays_updated":
        onHolidaysUpdated();
        break;
      case "workorders_job_ran":
        onWorkordersJobRan(json.data.total);
        break;
      case "workorders_job_warning":
        onWorkordersJobWarning(json.data.total);
        break;
      case "emergency_details_warning":
        onEmergencyDetailsWarning(json.data.user, json.data.retrieved_user);
        break;
      case "emergency_details_updated":
        onEmergencyDetailsUpdated(json.data.user);
        refresh("employee");
        break;
      case "project_visit_created":
        onVisitCreated();
        refresh("project_visit");
        break;
      case "project_visit_updated":
        onVisitUpdated();
        refresh("project_visit");

        break;
      case "project_visit_deleted":
        onVisitDeleted();
        refresh("project_visit");

        break;
      case "relations_deleted":
        onRelationsDeleted();
        refresh("relation");

        break;
      case "todo_updated":
        refresh("todo");
        break;
    }
  });
  return <></>;
};
